import React,{useState,useEffect,useCallback} from 'react';
import {categoryAPI,uomAPI,brandAPI,customerGroupAPI,uploadAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput} from '../components/common';
import {Plus,Edit,Trash2,ToggleLeft,ToggleRight,Layers,Ruler,Tag,Users,Upload,Image as ImgIcon} from 'lucide-react';
import {useLang} from '../i18n';
import toast from 'react-hot-toast';

// ═══════════════════════════════════════════════════════════
// IMAGE UPLOAD HELPER
// ═══════════════════════════════════════════════════════════
function ImageUpload({value,onChange,label='Image',size=80}){
  const{t}=useLang();
  const[uploading,setUploading]=useState(false);
  const handleFile=async e=>{
    const file=e.target.files?.[0];if(!file)return;
    if(file.size>5*1024*1024)return toast.error(t('common.max5mb'));
    setUploading(true);
    try{const r=await uploadAPI.single(file);onChange(r.data.url);toast.success(t('common.uploaded'))}
    catch{toast.error(t('common.uploadFailed'))}finally{setUploading(false)}
  };
  return(
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
      <div style={{width:size,height:size,borderRadius:8,border:'2px dashed var(--border)',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden',background:'var(--s2)',cursor:'pointer',position:'relative'}}>
        {value?<img src={value} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:
          uploading?<div className="text-dim" style={{fontSize:10}}>Uploading...</div>:<ImgIcon size={size/3} color="var(--td)"/>}
        <input type="file" accept="image/*" onChange={handleFile} style={{position:'absolute',inset:0,opacity:0,cursor:'pointer'}}/>
      </div>
      <span className="text-dim" style={{fontSize:10}}>{label}</span>
      {value&&<button className="btn btn-ghost btn-sm" onClick={()=>onChange('')} style={{fontSize:10,padding:'2px 8px'}}>{t('common.remove')}</button>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CATEGORIES TAB
// ═══════════════════════════════════════════════════════════
function CategoriesTab(){const{t}=useLang();
  const[data,setData]=useState([]);const[ld,setLd]=useState(true);const[search,setSearch]=useState('');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);

  const load=useCallback(async()=>{try{const r=await categoryAPI.getAll({search,limit:200});setData(r.data.data||r.data)}catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)}},[search]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const parentOpts=[{value:'',label:t('masterData.noneTopLevel')},...data.filter(c=>!c.parent_id).map(c=>({value:String(c.id),label:c.name}))];
  const blank={name:'',description:'',parent_id:'',image_url:'',sort_order:0,is_active:true};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{if(!form.name)return toast.error(t('masterData.nameRequired'));try{const d={...form,parent_id:parseInt(form.parent_id)||null,sort_order:parseInt(form.sort_order)||0};if(modal==='new'){await categoryAPI.create(d);toast.success(t('common.created'))}else{await categoryAPI.update(form.id,d);toast.success(t('common.updated'))}setModal(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const toggle=async c=>{try{await categoryAPI.update(c.id,{is_active:!c.is_active});toast.success(c.is_active?'Disabled':'Enabled');load()}catch{toast.error(t('common.failedToLoad'))}};
  const del=async()=>{try{await categoryAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};

  const topCats=data.filter(c=>!c.parent_id);
  const getChildren=pid=>data.filter(c=>c.parent_id===pid);

  return(<div>
    <div className="flex justify-between items-center" style={{marginBottom:14}}>
      <div style={{flex:1,maxWidth:400}}><SearchBar value={search} onChange={setSearch} placeholder={t('masterData.searchCategories')}/></div>
      <button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}><Plus size={14}/> Add Category</button>
    </div>

    {/* Tree View */}
    <div style={{display:'grid',gap:10,marginBottom:20}}>
      {topCats.map(cat=>{
        const children=getChildren(cat.id);
        return(
          <div key={cat.id} className="card" style={{padding:0,overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'var(--s2)'}}>
              {cat.image_url?<img src={cat.image_url} alt="" style={{width:40,height:40,borderRadius:6,objectFit:'cover'}}/>:<div style={{width:40,height:40,borderRadius:6,background:'var(--surface)',display:'flex',alignItems:'center',justifyContent:'center'}}><Layers size={18} color="var(--td)"/></div>}
              <div style={{flex:1}}>
                <div className="font-bold">{cat.name}</div>
                {cat.description&&<div className="text-dim" style={{fontSize:11}}>{cat.description}</div>}
              </div>
              <Badge variant={cat.is_active?'success':'danger'}>{cat.is_active?t('common.active'):t('common.inactive')}</Badge>
              <span className="text-dim" style={{fontSize:11}}>Sort: {cat.sort_order}</span>
              <div className="flex gap-2">
                <button className="btn btn-ghost btn-sm" onClick={()=>toggle(cat)}>{cat.is_active?<ToggleRight size={14} color="var(--success)"/>:<ToggleLeft size={14}/>}</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...cat,parent_id:cat.parent_id||''});setModal('edit')}}><Edit size={14}/></button>
                <button className="btn btn-danger btn-sm" onClick={()=>setDelId(cat.id)}><Trash2 size={14}/></button>
              </div>
            </div>
            {children.length>0&&<div style={{padding:'0 16px 12px',paddingTop:8}}>
              {children.map(sub=>(
                <div key={sub.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderRadius:6,marginBottom:4,background:'var(--surface)',border:'1px solid var(--border)'}}>
                  <div style={{width:4,height:24,background:'var(--primary)',borderRadius:2,marginRight:4}}/>
                  {sub.image_url?<img src={sub.image_url} alt="" style={{width:28,height:28,borderRadius:4,objectFit:'cover'}}/>:<Layers size={14} color="var(--td)"/>}
                  <div style={{flex:1}}>
                    <div className="font-bold" style={{fontSize:13}}>{sub.name}</div>
                    {sub.description&&<div className="text-dim" style={{fontSize:10}}>{sub.description}</div>}
                  </div>
                  <Badge variant={sub.is_active?'success':'danger'}>{sub.is_active?t('common.active'):t('common.inactive')}</Badge>
                  <div className="flex gap-2">
                    <button className="btn btn-ghost btn-sm" onClick={()=>toggle(sub)}>{sub.is_active?<ToggleRight size={12} color="var(--success)"/>:<ToggleLeft size={12}/>}</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...sub,parent_id:sub.parent_id||''});setModal('edit')}}><Edit size={12}/></button>
                    <button className="btn btn-danger btn-sm" onClick={()=>setDelId(sub.id)}><Trash2 size={12}/></button>
                  </div>
                </div>
              ))}
            </div>}
          </div>
        );
      })}
    </div>

    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?t('masterData.newCategory'):t('masterData.editCategory')} width={520}>
      <div style={{display:'flex',gap:16,marginBottom:16}}>
        <ImageUpload value={form.image_url||''} onChange={v=>F('image_url',v)} label={t('masterData.categoryImage')} size={90}/>
        <div style={{flex:1}}>
          <FormInput label={t('masterData.categoryName')} value={form.name||''} onChange={v=>F('name',v)} required/>
          <FormInput label={t('masterData.parentCategory')} value={String(form.parent_id||'')} onChange={v=>F('parent_id',v)} options={parentOpts}/>
        </div>
      </div>
      <div className="form-grid">
        <FormInput label={t('common.sortOrder')} value={String(form.sort_order||0)} onChange={v=>F('sort_order',v)} type="number"/>
        <FormInput label="Status" value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')} options={[{value:'true',label:'Active'},{value:'false',label:'Inactive'}]}/>
      </div>
      <FormInput label={t('common.description')} value={form.description||''} onChange={v=>F('description',v)} type="textarea"/>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{t('common.save')}</button></div>
    </Modal>
    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Category" width={400}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this category? Subcategories will become top-level. Products won't be deleted.</p>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button><button className="btn btn-danger" onClick={del}>{t('common.delete')}</button></div>
    </Modal>
  </div>);
}

// ═══════════════════════════════════════════════════════════
// UOM TAB
// ═══════════════════════════════════════════════════════════
function UomsTab(){
  const {t} = useLang();
  const[data,setData]=useState([]);const[ld,setLd]=useState(true);const[search,setSearch]=useState('');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);

  const load=useCallback(async()=>{try{const r=await uomAPI.getAll({search,limit:200});setData(r.data.data||r.data)}catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)}},[search]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const baseOpts=[{value:'',label:'— None (is base unit) —'},...data.filter(u=>!u.base_uom_id).map(u=>({value:String(u.id),label:`${u.name} (${u.abbreviation})`}))];
  const blank={name:'',abbreviation:'',base_uom_id:'',conversion_factor:1};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{if(!form.name||!form.abbreviation)return toast.error(t('masterData.nameAbbrRequired'));try{const d={...form,base_uom_id:parseInt(form.base_uom_id)||null,conversion_factor:parseFloat(form.conversion_factor)||1};if(modal==='new'){await uomAPI.create(d);toast.success(t('common.created'))}else{await uomAPI.update(form.id,d);toast.success(t('common.updated'))}setModal(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const del=async()=>{try{await uomAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};

  // Group by base unit
  const baseUnits=data.filter(u=>!u.base_uom_id);
  const getDerived=bid=>data.filter(u=>u.base_uom_id===bid);

  return(<div>
    <div className="flex justify-between items-center" style={{marginBottom:14}}>
      <div style={{flex:1,maxWidth:400}}><SearchBar value={search} onChange={setSearch} placeholder={t('masterData.searchUom')}/></div>
      <button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}><Plus size={14}/> Add UoM</button>
    </div>

    {/* Grouped display */}
    <div style={{display:'grid',gap:12}}>
      {baseUnits.map(base=>{
        const derived=getDerived(base.id);
        return(
          <div key={base.id} className="card" style={{padding:0,overflow:'hidden'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'var(--s2)'}}>
              <Ruler size={20} color="var(--accent)"/>
              <div style={{flex:1}}>
                <div className="font-bold">{base.name} <Badge variant="accent">{base.abbreviation}</Badge></div>
                <div className="text-dim" style={{fontSize:11}}>Base Unit · Factor: 1</div>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...base,base_uom_id:''});setModal('edit')}}><Edit size={14}/></button>
                <button className="btn btn-danger btn-sm" onClick={()=>setDelId(base.id)}><Trash2 size={14}/></button>
              </div>
            </div>
            {derived.length>0&&<div style={{padding:'8px 16px 12px'}}>
              <table style={{width:'100%',fontSize:13}}>
                <thead><tr style={{color:'var(--tm)',fontSize:11}}>
                  <th style={{textAlign:'left',padding:'4px 8px'}}>Derived Unit</th>
                  <th style={{textAlign:'left',padding:'4px 8px'}}>Abbreviation</th>
                  <th style={{textAlign:'left',padding:'4px 8px'}}>Conversion</th>
                  <th style={{textAlign:'left',padding:'4px 8px'}}>Meaning</th>
                  <th style={{textAlign:'right',padding:'4px 8px'}}>Actions</th>
                </tr></thead>
                <tbody>{derived.map(u=>(
                  <tr key={u.id} style={{borderTop:'1px solid var(--border)'}}>
                    <td style={{padding:'8px'}}><span className="font-bold">{u.name}</span></td>
                    <td style={{padding:'8px'}}><Badge variant="primary">{u.abbreviation}</Badge></td>
                    <td style={{padding:'8px'}}><span className="font-bold text-accent">{u.conversion_factor}x</span></td>
                    <td style={{padding:'8px'}}><span className="text-dim">1 {u.abbreviation} = {u.conversion_factor} {base.abbreviation}</span></td>
                    <td style={{padding:'8px',textAlign:'right'}}>
                      <div className="flex gap-2" style={{justifyContent:'flex-end'}}>
                        <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...u,base_uom_id:String(u.base_uom_id||'')});setModal('edit')}}><Edit size={12}/></button>
                        <button className="btn btn-danger btn-sm" onClick={()=>setDelId(u.id)}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>}
            {derived.length===0&&<div style={{padding:'10px 16px',color:'var(--td)',fontSize:12}}>No derived units yet</div>}
          </div>
        );
      })}
      {/* Orphan units */}
      {data.filter(u=>u.base_uom_id&&!baseUnits.find(b=>b.id===u.base_uom_id)).length>0&&
        <div className="card"><h4 style={{fontSize:13,fontWeight:700,marginBottom:8}}>Other Units</h4>
          <DataTable columns={[
            {key:'name',label:'Name',render:r=><span className="font-bold">{r.name}</span>},
            {key:'abbreviation',label:'Abbr',render:r=><Badge variant="primary">{r.abbreviation}</Badge>},
            {key:'conversion_factor',label:'Factor',render:r=><span className="font-bold">{r.conversion_factor}</span>},
          ]} data={data.filter(u=>u.base_uom_id&&!baseUnits.find(b=>b.id===u.base_uom_id))} actions={r=>(<div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...r,base_uom_id:String(r.base_uom_id||'')});setModal('edit')}}><Edit size={12}/></button>
            <button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={12}/></button>
          </div>)}/>
        </div>
      }
    </div>

    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?t('masterData.newUom'):t('masterData.editUom')} width={480}>
      <div className="form-grid">
        <FormInput label={t('masterData.unitName')} value={form.name||''} onChange={v=>F('name',v)} required placeholder="e.g. Box, Carton"/>
        <FormInput label={t('masterData.abbreviation')} value={form.abbreviation||''} onChange={v=>F('abbreviation',v)} required placeholder="e.g. box, ctn"/>
        <FormInput label="Base Unit" value={String(form.base_uom_id||'')} onChange={v=>F('base_uom_id',v)} options={baseOpts}/>
        <FormInput label={t('masterData.conversionFactor')} value={String(form.conversion_factor||1)} onChange={v=>F('conversion_factor',v)} type="number"/>
      </div>
      {form.base_uom_id&&form.abbreviation&&<div style={{padding:12,background:'var(--s2)',borderRadius:8,marginTop:8,fontSize:13}}>
        <span className="text-dim">Conversion: </span><span className="font-bold text-accent">1 {form.abbreviation} = {form.conversion_factor||1} {data.find(u=>u.id===parseInt(form.base_uom_id))?.abbreviation||'base'}</span>
      </div>}
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{t('common.save')}</button></div>
    </Modal>
    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete UoM" width={400}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this unit? Products using it won't be affected.</p>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button><button className="btn btn-danger" onClick={del}>{t('common.delete')}</button></div>
    </Modal>
  </div>);
}

// ═══════════════════════════════════════════════════════════
// BRANDS TAB
// ═══════════════════════════════════════════════════════════
function BrandsTab(){const{t}=useLang();
  const[data,setData]=useState([]);const[ld,setLd]=useState(true);const[search,setSearch]=useState('');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);

  const load=useCallback(async()=>{try{const r=await brandAPI.getAll({search,limit:200});setData(r.data.data||r.data)}catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)}},[search]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const blank={name:'',logo_url:'',description:'',website:'',country:'',is_active:true};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{if(!form.name)return toast.error(t('masterData.nameRequired'));try{if(modal==='new'){await brandAPI.create(form);toast.success(t('common.created'))}else{await brandAPI.update(form.id,form);toast.success(t('common.updated'))}setModal(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const toggle=async b=>{try{await brandAPI.update(b.id,{is_active:!b.is_active});toast.success(b.is_active?'Disabled':'Enabled');load()}catch{toast.error(t('common.failedToLoad'))}};
  const del=async()=>{try{await brandAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};

  return(<div>
    <div className="flex justify-between items-center" style={{marginBottom:14}}>
      <div style={{flex:1,maxWidth:400}}><SearchBar value={search} onChange={setSearch} placeholder={t('masterData.searchBrands')}/></div>
      <button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}><Plus size={14}/> Add Brand</button>
    </div>
    <DataTable loading={ld} columns={[
      {key:'logo',label:'',render:r=>r.logo_url?<img src={r.logo_url} alt="" style={{width:36,height:36,borderRadius:6,objectFit:'contain',background:'#fff',padding:2}}/>:<div style={{width:36,height:36,borderRadius:6,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center'}}><Tag size={14} color="var(--td)"/></div>},
      {key:'name',label:'Brand',render:r=><div><div className="font-bold">{r.name}</div>{r.description&&<div className="text-dim" style={{fontSize:11}}>{r.description}</div>}</div>},
      {key:'country',label:'Country'},{key:'website',label:'Website',render:r=>r.website?<a href={r.website} target="_blank" rel="noreferrer" style={{fontSize:12}}>{r.website.replace(/https?:\/\//,'')}</a>:'—'},
      {key:'is_active',label:'Status',render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?t('common.active'):t('common.inactive')}</Badge>},
    ]} data={data} actions={r=>(<div className="flex gap-2">
      <button className="btn btn-ghost btn-sm" onClick={()=>toggle(r)}>{r.is_active?<ToggleRight size={16} color="var(--success)"/>:<ToggleLeft size={16}/>}</button>
      <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...r});setModal('edit')}}><Edit size={14}/></button>
      <button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={14}/></button>
    </div>)}/>
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?t('masterData.newBrand'):t('masterData.editBrand')} width={520}>
      <div style={{display:'flex',gap:16,marginBottom:14}}>
        <ImageUpload value={form.logo_url||''} onChange={v=>F('logo_url',v)} label="Brand Logo" size={80}/>
        <div style={{flex:1}}>
          <FormInput label={t('common.name')} value={form.name||''} onChange={v=>F('name',v)} required/>
          <FormInput label="Country" value={form.country||''} onChange={v=>F('country',v)}/>
        </div>
      </div>
      <div className="form-grid">
        <FormInput label={t('common.website')} value={form.website||''} onChange={v=>F('website',v)} placeholder="https://..."/>
        <FormInput label="Status" value={form.is_active?'true':'false'} onChange={v=>F('is_active',v==='true')} options={[{value:'true',label:'Active'},{value:'false',label:'Inactive'}]}/>
      </div>
      <FormInput label={t('common.description')} value={form.description||''} onChange={v=>F('description',v)} type="textarea"/>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{t('common.save')}</button></div>
    </Modal>
    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Brand" width={400}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this brand?</p>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button><button className="btn btn-danger" onClick={del}>{t('common.delete')}</button></div>
    </Modal>
  </div>);
}

// ═══════════════════════════════════════════════════════════
// CUSTOMER GROUPS TAB
// ═══════════════════════════════════════════════════════════
function CustomerGroupsTab(){const{t}=useLang();
  const[data,setData]=useState([]);const[ld,setLd]=useState(true);const[search,setSearch]=useState('');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);

  const load=useCallback(async()=>{try{const r=await customerGroupAPI.getAll({search,limit:200});setData(r.data.data||r.data)}catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)}},[search]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const blank={name:'',discount_percent:0,description:''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{if(!form.name)return toast.error(t('masterData.nameRequired'));try{const d={...form,discount_percent:parseFloat(form.discount_percent)||0};if(modal==='new'){await customerGroupAPI.create(d);toast.success(t('common.created'))}else{await customerGroupAPI.update(form.id,d);toast.success(t('common.updated'))}setModal(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};
  const del=async()=>{try{await customerGroupAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}};

  return(<div>
    <div className="flex justify-between items-center" style={{marginBottom:14}}>
      <div style={{flex:1,maxWidth:400}}><SearchBar value={search} onChange={setSearch} placeholder={t('masterData.searchGroups')}/></div>
      <button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}><Plus size={14}/> Add Group</button>
    </div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',gap:12}}>
      {data.map(g=>(
        <div key={g.id} className="card" style={{display:'flex',flexDirection:'column',gap:10}}>
          <div className="flex justify-between items-center">
            <div className="font-bold" style={{fontSize:15}}>{g.name}</div>
            <div className="flex gap-2">
              <button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...g});setModal('edit')}}><Edit size={12}/></button>
              <button className="btn btn-danger btn-sm" onClick={()=>setDelId(g.id)}><Trash2 size={12}/></button>
            </div>
          </div>
          <div style={{display:'flex',gap:16}}>
            <div style={{padding:12,borderRadius:8,background:'var(--s2)',flex:1,textAlign:'center'}}>
              <div className="text-dim" style={{fontSize:10}}>Discount</div>
              <div className="font-bold text-success" style={{fontSize:20}}>{g.discount_percent}%</div>
            </div>
            <div style={{padding:12,borderRadius:8,background:'var(--s2)',flex:1,textAlign:'center'}}>
              <div className="text-dim" style={{fontSize:10}}>Members</div>
              <div className="font-bold text-primary" style={{fontSize:20}}>{(g.customers||[]).length}</div>
            </div>
          </div>
          {g.description&&<div className="text-dim" style={{fontSize:12}}>{g.description}</div>}
        </div>
      ))}
      {!ld&&data.length===0&&<div className="text-dim" style={{padding:40,textAlign:'center',gridColumn:'1 / -1'}}>No customer groups yet. Create one to assign discounts.</div>}
    </div>
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?'New Customer Group':'Edit Customer Group'} width={450}>
      <FormInput label={t('masterData.groupName')} value={form.name||''} onChange={v=>F('name',v)} required placeholder="e.g. VIP, Wholesale, Regular"/>
      <FormInput label={t('masterData.discountPercent')} value={String(form.discount_percent||0)} onChange={v=>F('discount_percent',v)} type="number" placeholder="Discount percentage for this group"/>
      <FormInput label={t('common.description')} value={form.description||''} onChange={v=>F('description',v)} type="textarea" placeholder={t('masterData.optionalDescription')}/>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setModal(null)}>{t('common.cancel')}</button><button className="btn btn-primary" onClick={save}>{t('common.save')}</button></div>
    </Modal>
    <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Group" width={400}>
      <p className="text-muted" style={{marginBottom:18}}>Delete this group? Customers in this group will be unlinked.</p>
      <div className="modal-footer"><button className="btn btn-ghost" onClick={()=>setDelId(null)}>{t('common.cancel')}</button><button className="btn btn-danger" onClick={del}>{t('common.delete')}</button></div>
    </Modal>
  </div>);
}

// ═══════════════════════════════════════════════════════════
// MAIN MASTER DATA PAGE
// ═══════════════════════════════════════════════════════════
export default function MasterDataPage(){
  const[tab,setTab]=useState('categories');
  const {t} = useLang();
  const tabs=[
    {id:'categories',label:t('masterData.categories'),icon:Layers,count:null},
    {id:'uoms',label:t('masterData.unitsOfMeasure'),icon:Ruler},
    {id:'brands',label:t('masterData.brands'),icon:Tag},
    {id:'customerGroups',label:t('masterData.customerGroups'),icon:Users},
  ];
  return(<div>
    <PageHeader title={t('masterData.title')} subtitle={t('masterData.subtitle')}/>
    <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
      {tabs.map(t=>(
        <button key={t.id} className={`filter-btn ${tab===t.id?'active':''}`} onClick={()=>setTab(t.id)} style={{display:'flex',alignItems:'center',gap:6}}>
          <t.icon size={14}/> {t.label}
        </button>
      ))}
    </div>
    {tab==='categories'&&<CategoriesTab/>}
    {tab==='uoms'&&<UomsTab/>}
    {tab==='brands'&&<BrandsTab/>}
    {tab==='customerGroups'&&<CustomerGroupsTab/>}
  </div>);
}
